// ─── Shared accessibility label maps and helpers ───────────────────────────
// Used by: map sidebar, experience detail, admin review, report dialog.
//
// The app deliberately does not render an "overall accessibility" judgment
// (Tillgänglig / Delvis / Ej tillgänglig). We extract neutral facts — door
// width, step count, surface type, etc. — and let users decide.

// ─── Door types ────────────────────────────────────────────────────────────

export const DOOR_TYPES = {
  manual_swing: "Manuell svängdörr",
  manual_sliding: "Manuell skjutdörr",
  automatic_sliding: "Automatisk skjutdörr",
  automatic_swing: "Automatisk svängdörr",
  revolving: "Karuselldörr",
  open: "Öppen (ingen dörr)",
  unknown: "Okänd"
};

// ─── Door width ────────────────────────────────────────────────────────────

export const DOOR_WIDTHS = {
  narrow_under_80cm: "Smal (<80 cm)",
  standard_80_90cm: "Standard (80–90 cm)",
  wide_over_90cm: "Bred (>90 cm)",
  unknown: "Okänd"
};

// ─── Surface types ─────────────────────────────────────────────────────────

export const SURFACE_TYPES = {
  asphalt: "Asfalt",
  concrete: "Betong",
  stone: "Sten/kullersten",
  gravel: "Grus",
  tiles: "Plattor",
  unknown: "Okänd"
};

// ─── Feature levels ─────────────────────────────────────────────────────────

export const FEATURE_LEVELS = {
  ground_level: "Marknivå",
  above_ground: "Över mark",
  below_ground: "Under mark"
};

/** @deprecated Use FEATURE_LEVELS */
export const ENTRANCE_LEVELS = FEATURE_LEVELS;

// ─── Step heights ──────────────────────────────────────────────────────────

export const STEP_HEIGHTS = {
  low_under_5cm: "Låg (<5 cm)",
  medium_5_15cm: "Medel (5–15 cm)",
  high_over_15cm: "Hög (>15 cm)",
  unknown: "Okänd"
};

// ─── Photo quality ─────────────────────────────────────────────────────────

export const PHOTO_QUALITY = {
  good: "Bra",
  acceptable: "Acceptabel",
  poor: "Dålig"
};

// ─── Parking-specific ──────────────────────────────────────────────────────

export const PARKING_SPACE_TYPES = {
  designated_accessible: "Handikappmärkt",
  near_entrance: "Nära entré",
  general: "Vanlig plats",
  unknown: "Okänd"
};
export const PARKING_PAYMENT = {
  free: "Gratis",
  paid: "Avgift",
  permit_required: "Tillstånd krävs",
  unknown: "Okänd"
};

/** Convert a measured distance in meters to a short Swedish label.
 *  Coordinate-derived (place center + approved entrance features). */
export function formatDistanceMeters(meters) {
  if (meters == null || !isFinite(meters)) return "Avstånd okänt";
  if (meters < 10) return "<10 m till entré";
  if (meters < 50) return `${Math.round(meters)} m till entré`;
  if (meters < 1000) return `${Math.round(meters / 5) * 5} m till entré`;
  return `${(meters / 1000).toFixed(1)} km till entré`;
}

/** Short version without "till entré" suffix — for compact fact rows. */
export function formatDistanceShort(meters) {
  if (meters == null || !isFinite(meters)) return null;
  if (meters < 10) return "<10 m";
  if (meters < 50) return `${Math.round(meters)} m`;
  if (meters < 1000) return `${Math.round(meters / 5) * 5} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

// ─── Key facts ──────────────────────────────────────────────────────────────
// Extracts the 2-3 most important facts as neutral text (no judgment)

export function getKeyFacts(a, featureTypeName, opts) {
  if (featureTypeName === "Parkering") return getParkingKeyFacts(a, opts?.distanceMeters);
  return getEntranceKeyFacts(a);
}
function getEntranceKeyFacts(a) {
  const facts = [];

  // Steps — always show (most important for mobility)
  if (a.hasSteps) {
    facts.push({
      icon: "steps",
      label: a.stepCount ? `${a.stepCount} trappsteg` : "Trappsteg"
    });
  } else {
    facts.push({
      icon: "steps",
      label: "Inga trappsteg"
    });
  }

  // Door type
  if (a.doorType && a.doorType !== "unknown") {
    const short = {
      manual_swing: "Manuell dörr",
      manual_sliding: "Skjutdörr",
      automatic_sliding: "Automatisk",
      automatic_swing: "Automatisk",
      revolving: "Karuselldörr",
      open: "Öppen ingång"
    };
    const label = short[a.doorType];
    if (label) facts.push({
      icon: "door",
      label
    });
  }

  // Width — only show if notably narrow or wide
  if (a.estimatedDoorWidth === "narrow_under_80cm") {
    facts.push({
      icon: "width",
      label: "<80 cm"
    });
  } else if (a.estimatedDoorWidth === "wide_over_90cm") {
    facts.push({
      icon: "width",
      label: ">90 cm"
    });
  }

  // Ramp
  if (a.hasRamp) {
    facts.push({
      icon: "ramp",
      label: "Ramp"
    });
  }
  return facts.slice(0, 3);
}
function getParkingKeyFacts(a, distanceMeters) {
  const facts = [];

  // Space type first — the big answer
  if (a.parkingSpaceType && a.parkingSpaceType !== "unknown") {
    const label = PARKING_SPACE_TYPES[a.parkingSpaceType];
    if (label) facts.push({
      icon: "parking",
      label
    });
  }

  // Distance to entrance — derived from coordinates
  const distShort = formatDistanceShort(distanceMeters);
  if (distShort) facts.push({
    icon: "distance",
    label: distShort
  });

  // Kerb cut — positive signal
  if (a.hasKerbCut === true) {
    facts.push({
      icon: "kerb",
      label: "Nedsänkt kant"
    });
  } else if (a.hasKerbCut === false) {
    facts.push({
      icon: "kerb",
      label: "Kantsten"
    });
  }

  // Payment — only show if not free (free = expected default)
  if (a.paymentType === "paid") {
    facts.push({
      icon: "payment",
      label: "Avgift"
    });
  } else if (a.paymentType === "permit_required") {
    facts.push({
      icon: "payment",
      label: "Tillstånd"
    });
  }
  return facts.slice(0, 3);
}